import { ref, reactive } from 'vue';
import FetchDataClient from '@/api/FetchDataClient';
import { useGameAudioStore } from '@/stores/gameAudioStore';

const api = new FetchDataClient();
const SYSTEM_AUDIO = '/assets/games/audio';

export function usePasswordReset() {
  const isLoading = ref(false);
  const errors = reactive<Record<string, string>>({});
  const successMessage = ref('');

  const clearErrors = () => {
    Object.keys(errors).forEach((key) => delete errors[key]);
  };

  const clearMessages = () => {
    successMessage.value = '';
    clearErrors();
  };

  const requestReset = async (email: string) => {
    isLoading.value = true;
    clearMessages();

    try {
      const result = await api.requestPasswordReset(email);
      successMessage.value = result.message;
      return { success: true };
    } catch (error: unknown) {
      console.error('Password reset request failed:', error);

      const gameAudio = useGameAudioStore();
      const errorMsg = error instanceof Error ? error.message : '';

      // Handle different error types
      if (errorMsg.includes('422')) {
        errors.email = "We can't find a user with that email address.";
        gameAudio.playSpeech(
          `${SYSTEM_AUDIO}/system-we-can-t-find-a-user-wit.mp3`
        );
      } else if (errorMsg.includes('401')) {
        errors.general = 'Authentication error occurred.';
        gameAudio.playSpeech(
          `${SYSTEM_AUDIO}/system-authentication-error-occ.mp3`
        );
      } else {
        errors.general = 'An unexpected error occurred. Please try again.';
        gameAudio.playSpeech(
          `${SYSTEM_AUDIO}/system-an-unexpected-error-occu.mp3`
        );
      }

      return { success: false, errors };
    } finally {
      isLoading.value = false;
    }
  };

  const resetPassword = async (
    token: string,
    email: string,
    password: string,
    passwordConfirmation: string
  ) => {
    isLoading.value = true;
    clearMessages();

    try {
      const result = await api.resetPassword(
        token,
        email,
        password,
        passwordConfirmation
      );
      successMessage.value = result.message;
      return { success: true };
    } catch (error: unknown) {
      console.error('Password reset failed:', error);

      const gameAudio = useGameAudioStore();
      const errorMsg = error instanceof Error ? error.message : '';

      // Handle different error types
      if (errorMsg.includes('422')) {
        if (errorMsg.includes('token')) {
          errors.token = 'This password reset token is invalid or has expired.';
          gameAudio.playSpeech(
            `${SYSTEM_AUDIO}/system-this-password-reset-toke.mp3`
          );
        } else if (errorMsg.includes('password')) {
          errors.password =
            'Password must be at least 8 characters and match confirmation.';
          gameAudio.playSpeech(
            `${SYSTEM_AUDIO}/system-password-must-be-at-leas.mp3`
          );
        } else {
          errors.email = 'Invalid email or token.';
          gameAudio.playSpeech(
            `${SYSTEM_AUDIO}/system-an-unexpected-error-occu.mp3`
          );
        }
      } else {
        errors.general = 'An unexpected error occurred. Please try again.';
        gameAudio.playSpeech(
          `${SYSTEM_AUDIO}/system-an-unexpected-error-occu.mp3`
        );
      }

      return { success: false, errors };
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    errors,
    successMessage,
    requestReset,
    resetPassword,
    clearMessages,
  };
}
